import { Moment } from 'moment';
import { ReactElement } from 'react';

import { FilterSuggestItemProps } from '../components/FilterControl/FilterSuggestItem/FilterSuggestItem';

import {
    AdvType,
    BannerStatusForFilter,
    CampaignPlatform,
    CampaignStatusForFilter,
    CampaignType
} from './domain';
import {
    CompleteReasonInput,
    DealTypeInput,
    GdAdGroupPrimaryStatusInput,
    GdCampaignStrategyTypeInput,
    GdGoalStatsFilterInput,
    GdiRecommendationType,
    GdRetargetingBaseStatusInput,
    GdShowConditionAutobudgetPriority,
    GdShowConditionAutobudgetPriorityInput,
    GdShowConditionPrimaryStatusInput,
    GdShowConditionTypeInput,
    GdSmartFilterBaseStatusInput,
    GdSmartFilterTargetFunnelInput,
    StatusDirectInput
} from './graphql-grid';

export enum Operator {
    EQUALS = 'EQUALS',
    NOT_EQUALS = 'NOT_EQUALS',
    LTE = 'LTE',
    GTE = 'GTE',
    CONTAINS = 'CONTAINS',
    NOT_CONTAINS = 'NOT_CONTAINS',
    COLON = 'COLON'
}

export enum CriteriaType {
    text = 'text',
    positiveNumber = 'positiveNumber',
    enumSet = 'enumSet',
    enumSetRadio = 'enumSetRadio',
    date = 'date',
    positiveNumberSet = 'positiveNumberSet',
    boolean = 'boolean'
}

export enum FilterExpressionValueType {
    partial = 'partial',
    partialDate = 'partialDate',
    text = 'text',
    enumSet = 'enumSet',
    enumSetRadio = 'enumSetRadio',
    numberSet = 'numberSet',
    number = 'number',
    boolean = 'boolean',
    date = 'date'
}

export enum CampaignFilterStrategyType {
    AVG_CLICK = 'AVG_CLICK',
    AVG_CPA = 'AVG_CPA',
    AVG_CPA_PER_CAMP = 'AVG_CPA_PER_CAMP',
    AVG_CPA_PER_FILTER = 'AVG_CPA_PER_FILTER',
    AVG_CPC_PER_CAMP = 'AVG_CPC_PER_CAMP',
    AVG_CPC_PER_FILTER = 'AVG_CPC_PER_FILTER',
    AVG_CPI = 'AVG_CPI',
    CPM_MAX_IMPRESSIONS = 'CPM_MAX_IMPRESSIONS',
    CPM_MAX_REACH = 'CPM_MAX_REACH',
    DEFAULT = 'DEFAULT',
    ROI = 'ROI',
    WEEK_BUDGET = 'WEEK_BUDGET',
    WEEK_BUNDLE = 'WEEK_BUNDLE',
    NO_PREMIUM = 'NO_PREMIUM'
}

export type EnumSetItem = { title: string; key: string };
export type FilterExpressionEnumSetValue = Array<EnumSetItem>;
export type FilterExpressionValueData =
    | string
    | number
    | boolean
    | number[]
    | string[]
    | Moment
    | FilterExpressionEnumSetValue
    | EnumSetItem;

export interface FilterExpressionValue {
    type: FilterExpressionValueType;
    value: FilterExpressionValueData;
}

export interface FilterExpression {
    criteriaKey: string;
    additionalCriteriaKey?: string;
    operator: Operator;
    value: FilterExpressionValue;
}

export interface AdditionalFilter {
    title?: string;
    key: string;
}

export interface FilterPartialExpression {
    field: string;
    additionalField?: AdditionalFilter;
    separatorField?: string;
    operator?: Operator | string;
    value?: FilterExpressionValue;
}

export interface ParsedFilterPartialExpression {
    field: string;
    additionalField?: string;
    separatorField?: string;
    operator?: Operator | string;
    value?: FilterExpressionValue;
}

export type ValueSuggest = (partial?: string) => Promise<FilterExpressionValue>;

export interface Criteria {
    title: string;
    key: string;
    type: CriteriaType;
    valueSuggest?: ValueSuggest;
    enumSet?: Array<EnumSetItem>;
    enumSetRadio?: Array<EnumSetItem>;
    enumSetFields?: Array<EnumSetItem>;
    validate?: (value: FilterExpressionValue) => boolean;
}

export interface CriteriaRawContainer {
    title: string;
    key: string;
    parent?: string;
    criteria: Array<string | CriteriaRawContainer>;
}

export interface CriteriaContainer {
    title: string;
    key: string;
    parent?: string;
    criteria: Array<Criteria | CriteriaContainer>;
}

export enum SuggestStep {
    container = 'container',
    field = 'field',
    operator = 'operator',
    value = 'value',
    fieldAdditional = 'fieldAdditional',
    separatorField = 'separatorField'
}

export interface StatsQueryFilter {
    maxShows?: number;
    minAvgClickCost?: number;
    minClicks?: number;
    minBounceRate?: number;
    maxGoals?: number;
    minRevenue?: number;
    minProfitability?: number;
    maxAvgGoalCost?: number;
    minAvgClickPosition?: number;
    maxRevenue?: number;
    maxBounceRate?: number;
    maxProfitability?: number;
    maxAvgClickPosition?: number;
    minCost?: number;
    minCpmPrice?: number;
    maxCpmPrice?: number;
    maxConversionRate?: number;
    minConversionRate?: number;
    minAvgDepth?: number;
    maxClicks?: number;
    maxCtr?: number;
    maxAvgClickCost?: number;
    maxAvgShowPosition?: number;
    minAvgGoalCost?: number;
    minCtr?: number;
    maxCost?: number;
    minAvgShowPosition?: number;
    minGoals?: number;
    minShows?: number;
    maxAvgDepth?: number;
    minCostPerAction?: number;
    maxCostPerAction?: number;
}

export interface GoalStatsQueryFilter {
    minCostPerAction?: number;
    maxCostPerAction?: number;
    maxConversionRate?: number;
    minConversionRate?: number;
    minGoals?: number;
    maxGoals?: number;
    goalId?: string;
}

export interface CampaignsQueryFilter {
    campaignIdIn?: number[];
    campaignIdNotIn?: number[];
    nameContains?: string;
    nameNotContains?: string;
    typeIn?: Array<CampaignType>;
    statusIn?: Array<CampaignStatusForFilter>;
    hasMetrika?: boolean;
    strategyTypeIn?: Array<GdCampaignStrategyTypeInput>;
    platformIn?: Array<CampaignPlatform>;
    recommendations?: Array<GdiRecommendationType>;
    maxStartDate?: string;
    maxFinishDate?: string;
    minStartDate?: string;
    minFinishDate?: string;
    hasBroadmatch?: boolean;
    hasSiteMonitoring?: boolean;
    currencyArchived?: boolean;
    // TODO: Не поддерживается бэком
    // hasDailyBudget?: boolean;
    hasBidModifiers?: boolean;
    archived?: boolean;
    calcType?: string;
    favorite?: boolean;
    stats?: StatsQueryFilter;
    goalStats?: GoalStatsQueryFilter[];
}

export interface GroupsQueryFilter {
    adGroupIdIn?: number[];
    adGroupIdNotIn?: number[];
    libraryMwIdIn?: number[];
    libraryMwIdNotIn?: number[];
    nameContains?: string;
    primaryStatusIn?: Array<GdAdGroupPrimaryStatusInput>;
    recommendations?: Array<GdiRecommendationType>;
    nameNotContains?: string;
    mwPackNameContains?: string;
    mwPackNameNotContains?: string;
    stats?: StatsQueryFilter;
    goalStats?: GoalStatsQueryFilter[];
    archived?: boolean;
}

export interface PhrasesQueryFilter {
    typeIn?: GdShowConditionTypeInput[];
    showConditionIdIn?: number[];
    showConditionIdNotIn?: number[];
    keywordContains?: string;
    keywordNotContains?: string;
    autobudgetPriorityIn?: Array<GdShowConditionAutobudgetPriority>;
    showConditionStatusIn?: GdShowConditionPrimaryStatusInput[];
    minPrice?: number;
    maxPrice?: number;
    minPriceContext?: number;
    maxPriceContext?: number;
    stats?: StatsQueryFilter;
    goalStats?: GoalStatsQueryFilter[];
}

export interface RetargetingsQueryFilter {
    nameContains?: string;
    nameNotContains?: string;
    statusIn?: GdRetargetingBaseStatusInput[];
    minPriceContext?: number;
    maxPriceContext?: number;
    stats?: StatsQueryFilter;
    // Для тайпингов retargetingsCriteriaMappings
    goalStats?: GoalStatsQueryFilter[];
}

export interface UserProfileQueryFilter {
    nameContains?: string;
    nameNotContains?: string;
    minPriceContext?: number;
    maxPriceContext?: number;
    stats?: StatsQueryFilter;
    goalStats?: GoalStatsQueryFilter[];
}

export interface FeedFiltersQueryFilter {
    nameContains?: string;
    nameNotContains?: string;
    targetFunnelIn?: Array<GdSmartFilterTargetFunnelInput>;
    smartFilterIdIn?: Array<number>;
    campaignIdIn?: Array<number>;
    maxPriceCpa?: number;
    minPriceCpa?: number;
    stats?: StatsQueryFilter;
    statusIn?: Array<GdSmartFilterBaseStatusInput>;
    adGroupIdIn?: Array<number>;
    minPriceCpc?: number;
    autobudgetPriorityIn?: Array<GdShowConditionAutobudgetPriorityInput>;
    goalStats?: Array<GdGoalStatsFilterInput>;
    maxPriceCpc?: number;
}

export interface TargetingQueryFilter {
    showConditionIdIn?: number;
    autobudgetPriorityIn?: Array<GdShowConditionAutobudgetPriority>;
    minPrice?: number;
    maxPrice?: number;
    stats?: StatsQueryFilter;
    goalStats?: StatsQueryFilter[];
}

export interface BannersQueryFilter {
    recommendations?: Array<GdiRecommendationType>;
    primaryStatusContains?: Array<BannerStatusForFilter>;
    adIdIn?: number[];
    adIdNotIn?: number[];
    typeIn?: Array<AdvType>;
    titleContains?: string;
    titleNotContains?: string;
    titleExtensionContains?: string;
    titleExtensionNotContains?: string;
    bodyContains?: string;
    bodyNotContains?: string;
    sitelinksExists?: boolean;
    imageExists?: boolean;
    stats?: StatsQueryFilter;
    goalStats?: GoalStatsQueryFilter[];
    archived?: boolean;
    turbolandingsExist?: boolean;
}

export type SuggestItem = ReactElement<
    FilterSuggestItemProps & { ref?: string; key?: string }
>;

export interface LogQueryFilter {
    campaignIds?: Array<number>;
    adGroupIds?: Array<number>;
    adIds?: Array<number>;
    logins?: Array<string>;
    categories?: Array<string>;
    nameContains?: string;
    nameNotContains?: string;
}

export interface DealsQueryFilter {
    dealIdIncluded?: number[];
    dealIdExcluded?: number[];

    typeIn?: Array<DealTypeInput>;

    nameNotContains?: string;
    nameContains?: string;

    nameIn?: string[];
    nameNotIn?: string[];

    statusContains?: Array<StatusDirectInput>;
    completeReasonContains?: Array<CompleteReasonInput>;

    minShows?: number;
    maxShows?: number;

    minClicks?: number;
    maxClicks?: number;

    minCpc?: number;
    maxCpc?: number;

    minCpm?: number;
    maxCpm?: number;

    minCtr?: number;
    maxCtr?: number;

    minMinPrice?: number;
    maxMinPrice?: number;

    minSpent?: number;
    maxSpent?: number;

    minNumberOfLinkedCampaigns?: number;
    maxNumberOfLinkedCampaigns?: number;

    stats?: {};
    goalStats?: {};
}
